import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { useTutorial } from '@/components/tutorial/use-tutorial';
import { useCurrentUser } from '@/hooks/api/use-user';
import useIsPWA from '@/hooks/use-is-pwa';

const STORAGE_KEY = 'perso-v3-tutorial-completed';
const TWO_DAYS_MS = 2 * 24 * 60 * 60 * 1000;

const WelcomePopup = () => {
  const { startTutorial, isActive, showWelcome, setShowWelcome } = useTutorial();
  const isPWA = useIsPWA();
  const isMobile = isPWA || window.innerWidth < 1024;
  const createdAt = useCurrentUser().created_at;

  // Auto-start: show popup once for users created less than 2 days ago
  useEffect(() => {
    const completed = localStorage.getItem(STORAGE_KEY);
    if (completed || !createdAt) return;

    const createdAtMs = new Date(createdAt).getTime();
    if (Number.isNaN(createdAtMs)) return;
    if (Date.now() - createdAtMs >= TWO_DAYS_MS) return;

    // Mark as shown immediately so a refresh before any interaction
    // doesn't trigger the auto-popup again. The manual button still works.
    localStorage.setItem(STORAGE_KEY, 'true');
    const timer = setTimeout(() => setShowWelcome?.(true), 600);
    return () => clearTimeout(timer);
  }, [createdAt, setShowWelcome]);

  if (!showWelcome || isActive) return null;

  const handleStart = () => {
    setShowWelcome?.(false);
    startTutorial?.();
  };

  const handleDismiss = () => {
    setShowWelcome?.(false);
    localStorage.setItem(STORAGE_KEY, 'true');
  };

  const chips = [
    { emoji: '\uD83D\uDCCB', label: 'Contrats' },
    { emoji: '\u26A0\uFE0F', label: 'Sinistres' },
    { emoji: '\u26A1', label: 'Actions' },
    { emoji: '\uD83C\uDF81', label: 'Avantages' },
    { emoji: '\uD83D\uDCDE', label: 'Aide' },
  ];

  const content = (
    <>
      <div className="text-center mb-4">
        <span className="inline-flex items-center gap-1.5 bg-primary/10 text-primary text-[10px] font-bold tracking-widest uppercase px-4 py-1 rounded-full">
          {'\uD83C\uDD95'} Première connexion
        </span>
      </div>
      <div className="text-4xl text-center mb-4">{'\uD83C\uDFCD\uFE0F'}</div>
      <h2 className="text-xl font-extrabold text-center text-foreground mb-1.5 leading-tight">
        Votre espace,<br /><span className="text-primary">prêt à démarrer !</span>
      </h2>
      <p className="text-sm text-muted-foreground text-center leading-relaxed mb-6">
        Tout ce dont vous avez besoin est ici.<br />
        On vous montre l'essentiel en 1 minute chrono.
      </p>
      <div className="flex flex-wrap justify-center gap-1.5 mb-12">
        {chips.map((chip) => (
          <span key={chip.label} className="inline-flex items-center gap-1 bg-muted px-2.5 py-1 rounded-lg text-xs text-muted-foreground">
            {chip.emoji} {chip.label}
          </span>
        ))}
      </div>
      <div className="flex flex-col gap-2.5">
        <Button onClick={handleStart} className="w-full cursor-pointer text-white font-bold text-sm py-4">
          C'est parti ! {'\u2192'}
        </Button>
        <button
          onClick={handleDismiss}
          className="text-xs text-muted-foreground underline cursor-pointer bg-transparent border-none text-center"
        >
          Non merci, je me débrouille
        </button>
      </div>
    </>
  );

  if (isMobile) {
    // Bottom sheet
    return createPortal(
      <div className="fixed inset-0 z-[70] flex items-end" onClick={handleDismiss}>
        <div className="absolute inset-0 bg-black/50" />
        <div
          className="relative w-full bg-card rounded-t-3xl px-12 pb-9 pt-4 animate-[slideUp_0.38s_cubic-bezier(0.25,1,0.5,1)]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="w-9 h-1 bg-border rounded-full mx-auto mb-12" />
          {content}
        </div>
      </div>,
      document.body,
    );
  }

  // Desktop centered modal
  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center" onClick={handleDismiss}>
      <div className="absolute inset-0 bg-black/50" />
      <div
        className="relative bg-card rounded-2xl p-8 max-w-md w-full mx-6 shadow-2xl animate-[slideUp_0.38s_cubic-bezier(0.25,1,0.5,1)]"
        onClick={(e) => e.stopPropagation()}
      >
        {content}
      </div>
    </div>,
    document.body,
  );
};

export default WelcomePopup;
